import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ADMIN_TOKEN_KEY = "nn_admin_token";
export const setAdminToken = (token) => {
  if (token) localStorage.setItem(ADMIN_TOKEN_KEY, token);
  else localStorage.removeItem(ADMIN_TOKEN_KEY);
};
export const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 15000,
  withCredentials: true,
});

// Attach Bearer token (defence-in-depth in case the cookie is dropped — e.g.
// cross-site Safari ITP — the localStorage token still gets us through).
api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Bounce to /admin/login on 401 if user is currently inside /admin/*
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401 && typeof window !== "undefined" &&
        window.location.pathname.startsWith("/admin") &&
        !window.location.pathname.startsWith("/admin/login")) {
      setAdminToken(null);
      const redir = encodeURIComponent(window.location.pathname);
      window.location.replace(`/admin/login?next=${redir}`);
    }
    return Promise.reject(err);
  }
);

export const fetchScarcity = (city, vertical) =>
  api.get(`/scarcity`, { params: { city, vertical } }).then(r => r.data);

export const fetchWeather = (city) =>
  api.get(`/weather`, { params: { city } }).then(r => r.data);

export const fetchPublicStats = () =>
  api.get(`/public/stats`).then(r => r.data);

export const getDemoLine = () =>
  api.get(`/public/demo-line`).then(r => r.data);

export const fetchFoundingCount = () =>
  api.get(`/founding/count`).then(r => r.data);

export const fetchConfig = () =>
  api.get(`/config`).then(r => r.data);

export const subscribeComingSoon = (payload) =>
  api.post(`/coming-soon/subscribe`, payload).then(r => r.data);

export const submitContact = (payload) =>
  api.post(`/contact`, payload).then(r => r.data);

export const onboardStart = (payload) =>
  api.post(`/onboard-simple/start`, payload).then(r => r.data);

export const onboardComplete = (payload) =>
  api.post(`/onboard-simple/complete`, payload).then(r => r.data);

export const checkoutStart = (payload) =>
  api.post(`/checkout/start`, { ...payload, origin_url: typeof window !== "undefined" ? window.location.origin : undefined }).then(r => r.data);

export const checkoutStatus = (sessionId) =>
  api.get(`/checkout/status/${sessionId}`).then(r => r.data);

export const fetchAdminInbox = (params = {}) =>
  api.get(`/admin/inbox`, { params }).then(r => r.data);

export const replyMessage = (id, body) =>
  api.post(`/admin/inbox/${id}/reply`, { reply_body: body }).then(r => r.data);

export const markRead = (id) =>
  api.post(`/admin/inbox/${id}/read`).then(r => r.data);

export const archiveMessage = (id) =>
  api.post(`/admin/inbox/${id}/archive`).then(r => r.data);

export const fetchAdminStats = () =>
  api.get(`/admin/stats`).then(r => r.data);

export const fetchIntegrationStatus = () =>
  api.get(`/integrations/status`).then(r => r.data);

export const fetchIntegrationsHealth = (hours = 168) =>
  api.get(`/admin/integrations/health`, { params: { hours } }).then(r => r.data);

export const resolveFailure = (id) =>
  api.post(`/admin/integrations/failures/${id}/resolve`).then(r => r.data);


// Rep leaderboard
export const fetchRepLeaderboard = () =>
  api.get(`/admin/rep-leaderboard`).then(r => r.data);

export const fetchRepDetail = (slug) =>
  api.get(`/admin/rep-leaderboard/${slug}`).then(r => r.data);

export const registerRep = (payload) =>
  api.post(`/admin/rep-leaderboard/register`, payload).then(r => r.data);

export const markRepPaid = (payload) =>
  api.post(`/admin/rep-leaderboard/mark-paid`, payload).then(r => r.data);


// LDS + NCGE
export const computeLds = (contractorId, overrides = {}) =>
  api.post(`/contractor/${contractorId}/lds/compute`, overrides).then(r => r.data);

export const fetchLds = (contractorId) =>
  api.get(`/contractor/${contractorId}/lds`).then(r => r.data);

export const ncgeListCampaigns = (contractorId) =>
  api.get(`/contractor/${contractorId}/ncge/campaigns`).then(r => r.data);

export const ncgeCreateCampaign = (contractorId, payload) =>
  api.post(`/contractor/${contractorId}/ncge/campaign`, payload).then(r => r.data);

export const ncgeCheckout = (campaignId, originUrl) =>
  api.post(`/ncge/campaign/${campaignId}/checkout`, { origin_url: originUrl }).then(r => r.data);

export const fetchNcgeCampaign = (campaignId) =>
  api.get(`/ncge/campaign/${campaignId}`).then(r => r.data);

// NCGE admin
export const ncgeReviewQueue = (status = "pending_review") =>
  api.get(`/admin/ncge/review-queue`, { params: { status } }).then(r => r.data);

export const ncgeApproveCampaign = (id, payload) =>
  api.post(`/admin/ncge/campaign/${id}/approve`, payload).then(r => r.data);

export const ncgeRejectCampaign = (id, payload) =>
  api.post(`/admin/ncge/campaign/${id}/reject`, payload).then(r => r.data);

export const ncgeMarkLaunched = (id, payload) =>
  api.post(`/admin/ncge/campaign/${id}/launched`, payload).then(r => r.data);

export const ncgeRecordMetrics = (id, payload) =>
  api.post(`/admin/ncge/campaign/${id}/metrics`, payload).then(r => r.data);

export const ncgeGetSettings = () =>
  api.get(`/admin/ncge/settings`).then(r => r.data);

// Referral program
export const ensureReferralCode = (contractorId) =>
  api.post(`/contractor/${contractorId}/referrals/ensure-code`).then(r => r.data);

export const fetchReferralDashboard = (contractorId) =>
  api.get(`/contractor/${contractorId}/referrals`).then(r => r.data);

export const resolveReferralCode = (code) =>
  api.get(`/r/${code}`).then(r => r.data);

export const adminReferralPayouts = () =>
  api.get(`/admin/referrals/payouts`).then(r => r.data);

export const adminReferralMarkPaid = (creditId, payload) =>
  api.post(`/admin/referrals/payout/${creditId}/paid`, payload).then(r => r.data);


export const ncgeSetReviewerEmail = (email) =>
  api.post(`/admin/ncge/settings/reviewer-email`, { email }).then(r => r.data);


// Retention Save (Anniversary Deal)
export const fetchRetentionOffer = (token) =>
  api.get(`/retention/offer/${token}`).then(r => r.data);

export const acceptRetentionOffer = (token) =>
  api.post(`/retention/offer/${token}/accept`).then(r => r.data);

export const adminRetentionOffers = (status) =>
  api.get(`/admin/retention/offers`, { params: status ? { status } : {} }).then(r => r.data);

export const adminRetentionFire = (contractorId, reason = "admin_manual") =>
  api.post(`/admin/retention/fire-offer/${contractorId}`, null, { params: { reason } }).then(r => r.data);

export const adminRetentionDecline = (offerId, reason = "") =>
  api.post(`/admin/retention/offer/${offerId}/decline`, null, { params: { reason } }).then(r => r.data);

export const adminRetentionRevert = () =>
  api.post(`/admin/retention/revert-expired`).then(r => r.data);


// ---- Admin Auth ----
export const authLogin = (email, password) =>
  api.post(`/auth/login`, { email, password }).then(r => r.data);

export const authVerify2fa = (challenge_id, code) =>
  api.post(`/auth/verify-2fa`, { challenge_id, code }).then(r => r.data);

export const authResendOtp = (challenge_id) =>
  api.post(`/auth/resend-2fa`, { challenge_id }).then(r => r.data);

// Emergent-managed Google Sign-In (allowlist mode).  The frontend redirects
// to https://auth.emergentagent.com/?redirect=<origin>/admin/auth-callback,
// then posts the returned session_id back here so we can allowlist + issue
// our own JWT / kick off 2FA.
export const authGoogleExchange = (session_id) =>
  api.post(`/auth/google/exchange`, { session_id }).then(r => r.data);

export const authForgotPassword = (email, channel = "email") =>
  api.post(`/auth/forgot-password`, { email, channel }).then(r => r.data);

export const authResetPasswordPublic = (email, token, new_password, channel = "email") =>
  api.post(`/auth/reset-password`, { email, token, new_password, channel }).then(r => r.data);

export const authLogout = () =>
  api.post(`/auth/logout`).then(r => r.data);

export const authMe = () =>
  api.get(`/auth/me`).then(r => r.data);

export const authListUsers = () =>
  api.get(`/auth/users`).then(r => r.data);

export const authCreateUser = (payload) =>
  api.post(`/auth/users`, payload).then(r => r.data);

export const authUpdateUser = (id, payload) =>
  api.patch(`/auth/users/${id}`, payload).then(r => r.data);

export const authDeleteUser = (id) =>
  api.delete(`/auth/users/${id}`).then(r => r.data);

export const authResetPassword = (id, new_password) =>
  api.post(`/auth/users/${id}/reset-password`, { new_password }).then(r => r.data);

export const authChangeMyPassword = (current_password, new_password) =>
  api.post(`/auth/me/change-password`, { current_password, new_password }).then(r => r.data);

export const authPermissions = () =>
  api.get(`/auth/permissions`).then(r => r.data);

export const fetchMyRepStats = () =>
  api.get(`/me/rep-stats`).then(r => r.data);


// ---- Discount Codes ----
export const adminListDiscountCodes = () =>
  api.get(`/admin/discount-codes`).then(r => r.data);

export const adminCreateDiscountCode = (payload) =>
  api.post(`/admin/discount-codes`, payload).then(r => r.data);

export const adminUpdateDiscountCode = (id, payload) =>
  api.patch(`/admin/discount-codes/${id}`, payload).then(r => r.data);

export const adminDeleteDiscountCode = (id) =>
  api.delete(`/admin/discount-codes/${id}`).then(r => r.data);

export const adminListInstitutionalPayouts = (status) =>
  api.get(`/admin/institutional-payouts`, { params: status ? { status } : {} }).then(r => r.data);

export const adminMarkInstitutionalPaid = (payout_ids, paid_method = "manual", paid_notes = "") =>
  api.post(`/admin/institutional-payouts/mark-paid`, { payout_ids, paid_method, paid_notes }).then(r => r.data);

export const publicValidateDiscount = (code, plan) =>
  api.post(`/discount-codes/validate`, { code, plan }).then(r => r.data);


// ---- Business Health Score / Leakage Assessment ----
export const submitAssessment = (payload) =>
  api.post(`/assessments`, payload).then(r => r.data);

export const fetchAssessment = (id) =>
  api.get(`/assessments/${id}`).then(r => r.data);

export const smsAssessmentReport = (id, phone) =>
  api.post(`/assessments/${id}/sms`, { phone }).then(r => r.data);

export const emailAssessmentReport = (id, email) =>
  api.post(`/assessments/${id}/email`, { email }).then(r => r.data);


// ============================================================
// Available Today · Workforce Marketplace (Phase 1)
// ============================================================
export const registerWorker = (payload) =>
  api.post(`/workers/register`, payload).then(r => r.data);
export const getWorkerMe = () =>
  api.get(`/workers/me`).then(r => r.data);
export const updateWorkerMe = (payload) =>
  api.patch(`/workers/me`, payload).then(r => r.data);
export const setWorkerStatus = (available_today) =>
  api.post(`/workers/me/status`, { available_today }).then(r => r.data);

export const listWorkforceSkills = () =>
  api.get(`/workforce/skills`).then(r => r.data);

// Contractor Available-Today session
export const atStartSession = (email) =>
  api.post(`/available-today/session/start`, { email }).then(r => r.data);
export const atCheckSession = () =>
  api.get(`/available-today/session`).then(r => r.data);
export const atEndSession = () =>
  api.post(`/available-today/session/end`).then(r => r.data);

// Browse + favorites
export const atBrowseWorkers = (params = {}) => {
  const clean = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") clean[k] = v;
  });
  return api.get(`/available-today/workers`, { params: clean }).then(r => r.data);
};
export const atGetWorker = (id) =>
  api.get(`/available-today/workers/${id}`).then(r => r.data);
export const atFavoriteWorker = (id) =>
  api.post(`/available-today/favorites/${id}`).then(r => r.data);
export const atUnfavoriteWorker = (id) =>
  api.delete(`/available-today/favorites/${id}`).then(r => r.data);
export const atListFavorites = () =>
  api.get(`/available-today/favorites`).then(r => r.data);

// Labor requests (Phase 2)
export const atCreateLaborRequest = (payload) =>
  api.post(`/available-today/requests`, payload).then(r => r.data);
export const atListMyRequests = (limit = 40) =>
  api.get(`/available-today/requests`, { params: { limit } }).then(r => r.data);
export const atGetRequestDetail = (id) =>
  api.get(`/available-today/requests/${id}`).then(r => r.data);
export const atCancelRequest = (id) =>
  api.post(`/available-today/requests/${id}/cancel`).then(r => r.data);
// Public worker response endpoints (no auth)
export const laborOfferRead = (token) =>
  api.get(`/labor-response/${token}`).then(r => r.data);
export const laborOfferRespond = (token, decision) =>
  api.post(`/labor-response/${token}`, { decision }).then(r => r.data);

// Admin skills taxonomy
export const adminListSkills = () =>
  api.get(`/admin/workforce/skills`).then(r => r.data);
export const adminCreateSkill = (name, sort_order) =>
  api.post(`/admin/workforce/skills`, { name, sort_order }).then(r => r.data);
export const adminUpdateSkill = (id, payload) =>
  api.patch(`/admin/workforce/skills/${id}`, payload).then(r => r.data);
export const adminDeleteSkill = (id) =>
  api.delete(`/admin/workforce/skills/${id}`).then(r => r.data);

export const adminListAssessments = (params = {}) =>
  api.get(`/admin/assessments`, { params }).then(r => r.data);

export const fetchMyAssessmentStats = (repSlug) =>
  api.get(`/me/assessment-stats`, { params: repSlug ? { rep_slug: repSlug } : {} }).then(r => r.data);

export const adminListLeakageLookups = () =>
  api.get(`/admin/leakage-lookups`).then(r => r.data);

export const adminUpdateLeakageLookup = (payload) =>
  api.patch(`/admin/leakage-lookups`, payload).then(r => r.data);


// ============================================================
// RI Contractor Directory (public + admin)
// ============================================================
export const directoryMeta = () =>
  api.get(`/directory/meta`).then(r => r.data);
export const directorySearch = ({ trade, city, q, limit = 50 } = {}) =>
  api.get(`/directory/search`, { params: { trade, city, q, limit } }).then(r => r.data);
export const directoryGet = (id) =>
  api.get(`/directory/${id}`).then(r => r.data);
export const directoryClaim = (id, { email, phone }) =>
  api.post(`/directory/${id}/claim`, { email, phone }).then(r => r.data);

export const adminDirectoryStatus = () =>
  api.get(`/admin/directory/status`).then(r => r.data);
export const adminDirectoryImportOne = (trade, city) =>
  api.post(`/admin/directory/import`, { trade, city }).then(r => r.data);
export const adminDirectoryImportAll = ({ trades, cities, top_cities_only } = {}) =>
  api.post(`/admin/directory/import-all`, { trades, cities, top_cities_only }, { timeout: 600000 })
    .then(r => r.data);
export const adminDirectoryMarkPartner = (listing_id, nearnow_user_id) =>
  api.post(`/admin/directory/mark-partner`, { listing_id, nearnow_user_id }).then(r => r.data);

export const adminLaunchReadiness = () =>
  api.get(`/admin/launch-readiness`).then(r => r.data);

export const adminAdPreviewVariants = () =>
  api.get(`/admin/ad-preview/variants`).then(r => r.data);
export const adminAdPreview = (payload) =>
  api.post(`/admin/ad-preview`, payload).then(r => r.data);
